import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, CreditCard, MapPinned, MessageSquare, Save, Send, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  twilio_account_sid: '',
  twilio_api_key_sid: '',
  twilio_api_key_secret: '',
  twilio_from_number: '',
  sms_default_country_code: '263',
  google_maps_server_api_key: '',
  google_maps_browser_api_key: '',
  paystack_secret_key: '',
  paystack_callback_url: '',
  paynow_integration_id: '',
  paynow_integration_key: '',
  paynow_result_url: '',
  paynow_return_url: '',
};

const fieldGroups = [
  {
    id: 'sms',
    title: 'Twilio SMS',
    description: 'Twilio account credentials, sending number, and default country dialing code.',
    icon: MessageSquare,
    fields: [
      ['twilio_account_sid', 'Account SID', 'text'],
      ['twilio_api_key_sid', 'API Key SID', 'text'],
      ['twilio_api_key_secret', 'API Key secret', 'password'],
      ['twilio_from_number', 'Twilio sender number', 'tel'],
      ['sms_default_country_code', 'Default country code', 'text'],
    ],
  },
  {
    id: 'maps',
    title: 'Google Maps',
    description: 'Use a server key for Distance Matrix and a browser key restricted by website referrer for the fleet map.',
    icon: MapPinned,
    fields: [
      ['google_maps_server_api_key', 'Server API key', 'password'],
      ['google_maps_browser_api_key', 'Browser Maps JavaScript key', 'password'],
    ],
  },
  {
    id: 'paystack',
    title: 'Paystack',
    description: 'Secret key for hosted checkout and signed webhook verification.',
    icon: CreditCard,
    fields: [
      ['paystack_secret_key', 'Secret key', 'password'],
      ['paystack_callback_url', 'Return URL', 'url'],
    ],
  },
  {
    id: 'paynow',
    title: 'Paynow',
    description: 'Integration credentials and callback URLs for existing EcoCash, OneMoney, and card flows.',
    icon: CreditCard,
    fields: [
      ['paynow_integration_id', 'Integration ID', 'text'],
      ['paynow_integration_key', 'Integration key', 'password'],
      ['paynow_result_url', 'Result callback URL', 'url'],
      ['paynow_return_url', 'Return URL', 'url'],
    ],
  },
];

const secretFields = new Set([
  'twilio_api_key_secret',
  'google_maps_server_api_key',
  'google_maps_browser_api_key',
  'paystack_secret_key',
  'paynow_integration_key',
]);

const IntegrationsPage = () => {
  const { token } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [clearSecrets, setClearSecrets] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/integrations/settings`, { headers });
      setStatus(response.data);
      setForm(current => ({
        ...current,
        twilio_account_sid: response.data.sms.account_sid || '',
        twilio_api_key_sid: response.data.sms.api_key_sid || '',
        twilio_from_number: response.data.sms.from_number || '',
        sms_default_country_code: response.data.sms.default_country_code || '263',
        paystack_callback_url: response.data.payments.paystack_callback_url || '',
        paynow_result_url: response.data.payments.paynow_result_url || '',
        paynow_return_url: response.data.payments.paynow_return_url || '',
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load integration settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadSettings();
  }, [token]);

  const updateField = (name, value) => setForm(current => ({ ...current, [name]: value }));

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = Object.fromEntries(
      Object.entries(form).filter(([name, value]) => !secretFields.has(name) || value.trim())
    );
    payload.clear_secrets = clearSecrets;
    try {
      const response = await axios.post(`${API_URL}/integrations/settings`, payload, { headers });
      setStatus(response.data);
      setForm(current => Object.fromEntries(
        Object.entries(current).map(([name, value]) => [name, secretFields.has(name) ? '' : value])
      ));
      setClearSecrets([]);
      toast.success('Integration settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (provider) => {
    setTesting(provider);
    try {
      const payload = { provider };
      if (provider === 'sms') {
        payload.phone = testPhone;
        payload.message = 'Panashe Coaches integration test';
      }
      const response = await axios.post(`${API_URL}/integrations/test`, payload, { headers });
      toast.success(response.data.message || `${provider} test passed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `${provider} test failed`);
    } finally {
      setTesting('');
    }
  };

  const configured = (provider) => {
    if (provider === 'sms') return status?.sms.configured;
    if (provider === 'maps') return status?.maps.server_key_configured;
    if (provider === 'paystack') return status?.payments.paystack_configured;
    return status?.payments.paynow_configured;
  };

  const secretConfigured = (name) => {
    if (name === 'twilio_api_key_secret') return status?.sms.api_key_secret_configured;
    if (name === 'google_maps_server_api_key') return status?.maps.server_key_configured;
    if (name === 'google_maps_browser_api_key') return status?.maps.browser_key_configured;
    if (name === 'paystack_secret_key') return status?.payments.paystack_configured;
    return status?.payments.paynow_configured;
  };

  return (
    <DashboardLayout>
      <main className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-emerald-700">SYSTEM CONFIGURATION</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">Provider integrations</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">Manage SMS, Maps, and payment provider connections. Secret values are encrypted at rest and never shown after saving.</p>
          </div>
          <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${status?.encryption_ready ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            <ShieldCheck className="h-4 w-4" />
            {status?.encryption_ready ? 'Encrypted credential storage ready' : 'Set encryption key on the server to enable saving'}
          </div>
        </div>

        {!status?.encryption_ready && status && (
          <div className="mb-6 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
            Set <code>INTEGRATION_SETTINGS_ENCRYPTION_KEY</code> in the backend environment, then restart the API. Generate a key with <code>python -c &quot;from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())&quot;</code>.
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-sm text-slate-500">Loading provider settings…</div>
        ) : (
          <form onSubmit={saveSettings} className="divide-y divide-slate-200 border-y border-slate-200 bg-white px-4 md:px-6">
            {fieldGroups.map(group => {
              const Icon = group.icon;
              return (
                <section key={group.id} className="grid gap-5 py-6 md:grid-cols-[220px_1fr]">
                  <div>
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900"><Icon className="h-4 w-4 text-emerald-700" />{group.title}</h2>
                    <p className="mt-2 text-xs leading-5 text-slate-500">{group.description}</p>
                    {group.id !== 'maps' && (
                      <p className={`mt-3 text-xs font-medium ${configured(group.id) ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {configured(group.id) ? 'Credentials configured' : 'Not configured'}
                      </p>
                    )}
                    {group.id === 'maps' && (
                      <p className="mt-3 text-xs font-medium text-slate-600">
                        Server: {status?.maps.server_key_configured ? 'configured' : 'not configured'} · Browser: {status?.maps.browser_key_configured ? 'configured' : 'not configured'}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {group.fields.map(([name, label, type]) => (
                      <div key={name} className="space-y-1.5">
                        <Label htmlFor={name}>{label}</Label>
                        <div className="flex gap-2">
                          <Input
                            id={name}
                            type={type}
                            value={form[name]}
                            onChange={event => updateField(name, event.target.value)}
                            placeholder={secretFields.has(name) ? (secretConfigured(name) ? 'Leave blank to keep current key' : 'Enter provider key') : ''}
                            autoComplete={secretFields.has(name) ? 'new-password' : 'off'}
                          />
                          {secretFields.has(name) && secretConfigured(name) && (
                            <Button
                              type="button"
                              variant={clearSecrets.includes(name) ? 'destructive' : 'outline'}
                              aria-label={`${clearSecrets.includes(name) ? 'Keep' : 'Remove'} ${label}`}
                              title={`${clearSecrets.includes(name) ? 'Keep' : 'Remove'} stored ${label}`}
                              onClick={() => setClearSecrets(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name])}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {clearSecrets.includes(name) && <p className="text-xs text-red-700">This credential will be removed when you save.</p>}
                      </div>
                    ))}
                    {group.id === 'sms' && (
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="sms-test-phone">Test destination</Label>
                        <div className="flex flex-wrap gap-2">
                          <Input id="sms-test-phone" className="max-w-sm" value={testPhone} onChange={event => setTestPhone(event.target.value)} placeholder="+263 77 123 4567" />
                          <Button type="button" variant="outline" disabled={!configured('sms') || !testPhone || testing === 'sms'} onClick={() => runTest('sms')}>
                            <Send className="mr-2 h-4 w-4" />{testing === 'sms' ? 'Sending…' : 'Send test SMS'}
                          </Button>
                        </div>
                        <p className="text-xs text-amber-700">This sends a real SMS and may incur provider charges.</p>
                      </div>
                    )}
                    {group.id !== 'sms' && (
                      <div className="sm:col-span-2">
                        <Button type="button" variant="outline" disabled={!configured(group.id) || testing === group.id} onClick={() => runTest(group.id)}>
                          <Activity className="mr-2 h-4 w-4" />{testing === group.id ? 'Testing…' : `Test ${group.title}`}
                        </Button>
                        {group.id === 'maps' && <p className="mt-2 text-xs text-slate-500">Tests a live Distance Matrix request between Harare and Bulawayo.</p>}
                        {group.id === 'paystack' && <p className="mt-2 text-xs text-slate-500">Uses the read-only balance endpoint; it does not create a charge.</p>}
                        {group.id === 'paystack' && <p className="mt-2 text-xs text-slate-600">In Paystack Dashboard, register webhook URL <code>https://panashecoaches.com/api/payments/paystack/webhook</code> and enable <code>charge.success</code>.</p>}
                        {group.id === 'paynow' && <p className="mt-2 text-xs text-slate-500">Creates a 1.00 checkout session to test credentials; no charge occurs unless someone completes checkout.</p>}
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
            <div className="flex justify-end py-5">
              <Button type="submit" disabled={saving || !status?.encryption_ready}>
                <Save className="mr-2 h-4 w-4" />{saving ? 'Saving…' : 'Save integration settings'}
              </Button>
            </div>
          </form>
        )}
      </main>
    </DashboardLayout>
  );
};

export default IntegrationsPage;